import { useEffect, useState } from "react";
import { toast } from "sonner";
import { g, p, errMsg, TENANT_KEY } from "@/lib/api";
import { useI18n } from "@/i18n";
import { LEGALE_VERSIONE } from "@/data/legale";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Check, ArrowRight, ArrowLeft, Sparkles, Store, ConciergeBell, ChefHat, MapPin, Users, X, PartyPopper, Copy, ClipboardList } from "lucide-react";

const STEPS = 7;

/** Traccia fin dove arriva chi fa il test: se abbandona sappiamo a quale domanda. */
const quizSid = () => {
  let s = sessionStorage.getItem("rh_quiz_sid");
  if (!s) {
    s = `q${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("rh_quiz_sid", s);
  }
  return s;
};

const Chip = ({ on, children, ...rest }) => (
  <button
    type="button"
    {...rest}
    className={`rounded-full border px-4 py-2.5 text-sm font-semibold transition-all ${
      on ? "border-slate-900 bg-slate-900 text-white shadow-md" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
    }`}
  >
    {children}
  </button>
);

/**
 * Test attività: profila il locale e costruisce la configurazione.
 * publicMode = anteprima pubblica con creazione della demo di prova.
 */
export const SetupWizard = ({ publicMode = false, onDone, onClose }) => {
  const { t, lang } = useI18n();
  const [cat, setCat] = useState(null);
  const [step, setStep] = useState(0);
  const [giaAttiva, setGiaAttiva] = useState(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [preview, setPreview] = useState(null);
  const [bizName, setBizName] = useState("");
  const [vat, setVat] = useState("");
  const [email, setEmail] = useState("");
  const [accept, setAccept] = useState(false);
  const [a, setA] = useState({
    activities: [],
    other_text: "",
    services: ["tavolo"],
    departments: [],
    zones: ["Sala"],
    team_size: "2-3",
    outdoor_people: 0,
    floats_per_person: false,
    float_amount: 50,
    motto: "",
    extras: [],
    banco: [],
    preload_menu: true,
    menu_mode: "add",
    lang,
  });

  useEffect(() => {
    g(`/profile/catalog?lang=${lang}`).then(setCat);
    setA((s) => ({ ...s, lang }));
  }, [lang]);

  useEffect(() => {
    p("/quiz/track", { sid: quizSid(), step, event: "step", lang, public: publicMode }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const toggle = (key, v) =>
    setA((s) => ({ ...s, [key]: s[key].includes(v) ? s[key].filter((x) => x !== v) : [...s[key], v] }));

  // I reparti si precompilano dalle attività scelte, poi restano modificabili a mano.
  useEffect(() => {
    if (!cat) return;
    const auto = [...new Set(a.activities.map((id) => cat.activities.find((x) => x.id === id)?.dept).filter(Boolean))];
    setA((s) => ({ ...s, departments: auto }));
  }, [a.activities, cat]);

  const next = async () => {
    if (step === 0 && a.activities.length === 0) return toast.error(t("wiz.err.act"));
    if (step === 2 && a.departments.length === 0) return toast.error(t("wiz.err.dept"));
    if (step === STEPS - 2) {
      setBusy(true);
      try {
        setPreview(await p("/profile/suggest", a));
        setStep(step + 1);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    setStep(step + 1);
  };

  // Partita IVA italiana (11 cifre), codice fiscale (16) o VAT estera: 5-20 alfanumerici.
  const vatOk = (v) => {
    let n = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (n.startsWith("IT") && /^\d+$/.test(n.slice(2))) n = n.slice(2);
    if (!n) return false;
    return /^\d+$/.test(n) ? [11, 16].includes(n.length) : n.length >= 5 && n.length <= 20;
  };

  const startTrial = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) return toast.error(t("wiz.err.email"));
    if (!vatOk(vat)) return toast.error(t("wiz.err.vat"));
    if (!accept) return toast.error(t("wiz.err.accept"));
    setBusy(true);
    try {
      const r = await p("/tenant/trial", {
        business_name: bizName,
        vat: vat.trim(),
        email: email.trim(),
        profile: a,
        accept_terms: true,
        docs_version: LEGALE_VERSIONE,
        ref: sessionStorage.getItem("rh_src") || "",
      });
      p("/quiz/track", { sid: quizSid(), step: STEPS - 1, event: "completed", lang, public: publicMode }).catch(() => {});
      localStorage.setItem(TENANT_KEY, r.token);
      localStorage.setItem("rh_trial_info", JSON.stringify({ slug: r.slug, code: r.access_code, pin: r.owner_pin, days: r.days }));
      localStorage.removeItem("rh_steps_hidden");
      setCreated({ slug: r.slug, code: r.access_code, pin: r.owner_pin, days: r.days, email: email.trim() });
      setBusy(false);
    } catch (e) {
      const d = e?.response?.data?.detail || "";
      if (e?.response?.status === 409 && d.startsWith("already:")) {
        const [, nome, slug, ...resto] = d.split(":");
        setGiaAttiva({ nome, slug, messaggio: resto.join(":") || d });
      } else {
        toast.error(errMsg(e));
      }
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const r = await p("/profile/apply", a);
      p("/quiz/track", { sid: quizSid(), step: STEPS - 1, event: "completed", lang, public: publicMode }).catch(() => {});
      toast.success(r.menu_added ? `${t("wiz.apply")} · ${r.menu_added}` : t("wiz.apply"));
      onDone?.(r);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!cat) return <p className="p-8 text-sm text-slate-500">{t("wiz.loading")}</p>;

  // Schermata finale: le credenziali in chiaro, da copiare, e la conferma che sono anche nella posta.
  if (created)
    return (
      <div className="mx-auto w-full max-w-2xl" data-testid="trial-created">
        <Card className="overflow-hidden">
          <div className="bg-emerald-600 p-6 text-white">
            <PartyPopper className="h-7 w-7" />
            <h2 className="font-head mt-3 text-2xl font-bold">{t("done.title")}</h2>
            <p className="mt-1 text-sm text-emerald-50">{t("done.sub", { n: created.days })}</p>
          </div>
          <div className="space-y-4 p-6">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              {[["done.venue", created.slug], ["done.code", created.code], ["done.pin", created.pin]].map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3 border-b border-slate-200 py-2 last:border-0">
                  <span className="text-xs font-semibold text-slate-500">{t(k)}</span>
                  <span className="font-num text-lg font-bold tracking-[0.1em]" data-testid={`done-${k.split(".")[1]}`}>{v}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="h-12"
                data-testid="done-copy-btn"
                onClick={() => {
                  const txt = `${t("done.venue")}: ${created.slug}\n${t("done.code")}: ${created.code}\n${t("done.pin")}: ${created.pin}\n${window.location.origin}/entra`;
                  navigator.clipboard?.writeText(txt);
                  toast.success(t("done.copied"));
                }}
              >
                <Copy className="mr-1.5 h-4 w-4" /> {t("done.copy")}
              </Button>
              <Button className="h-12 flex-1" data-testid="done-enter-btn" onClick={() => { window.location.href = "/"; }}>
                {t("done.enter")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>

            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900" data-testid="done-mail-note">
              {t("done.mail", { mail: created.email })}
            </p>
            <p className="text-xs text-slate-500">{t("done.next")}</p>
          </div>
        </Card>
      </div>
    );

  // Registrazione ripetuta: niente doppioni, gli si rimandano le credenziali di quella che ha già.
  if (giaAttiva)
    return (
      <div className="mx-auto w-full max-w-2xl" data-testid="trial-already">
        <Card className="overflow-hidden">
          <div className="bg-amber-500 p-6 text-slate-950">
            <PartyPopper className="h-7 w-7" />
            <h2 className="font-head mt-3 text-2xl font-bold">
              {giaAttiva.nome ? "Hai già una prova attiva" : "Partita IVA già registrata"}
            </h2>
            {giaAttiva.nome && <p className="mt-1 text-sm">{giaAttiva.nome}</p>}
          </div>
          <div className="space-y-4 p-6">
            <p className="text-sm text-slate-600">{giaAttiva.messaggio}</p>
            {giaAttiva.slug && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-xs font-semibold text-slate-500">{t("done.venue")}</span>
                  <span className="font-num text-lg font-bold tracking-[0.1em]" data-testid="already-slug">{giaAttiva.slug}</span>
                </div>
              </div>
            )}
            <Button className="h-12 w-full" data-testid="already-enter-btn" onClick={() => { window.location.href = "/entra"; }}>
              Vai all'accesso <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <button className="w-full text-xs font-semibold text-slate-400" data-testid="already-back-btn" onClick={() => setGiaAttiva(null)}>
              Torna indietro
            </button>
          </div>
        </Card>
      </div>
    );

  const icons = [Store, ConciergeBell, ChefHat, MapPin, Users, ClipboardList, Sparkles];
  const Icon = icons[step];

  return (
    <div className="mx-auto w-full max-w-2xl" data-testid="setup-wizard">
      <Card className="overflow-hidden">
        <div className="flex items-start gap-4 bg-slate-900 p-6 text-white">
          <span className="rounded-xl bg-white/10 p-3">
            <Icon className="h-5 w-5 text-amber-400" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">
              {publicMode ? t("wiz.public") : t("wiz.setup")} ·{" "}
              {step === STEPS - 1 ? t("wiz.recap") : t("wiz.step", { n: step + 1, tot: STEPS - 1 })}
            </p>
            <h2 className="mt-1 text-lg font-bold md:text-lg">{t(`wiz.q${step}`)}</h2>
          </div>
          {onClose && (
            <button onClick={onClose} data-testid="setup-close-btn" className="rounded-md p-1.5 text-slate-400 hover:bg-white/10">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="h-1 bg-slate-100">
          <div className="h-1 bg-amber-400 transition-all" style={{ width: `${((step + 1) / STEPS) * 100}%` }} />
        </div>

        <div className="space-y-5 p-6">
          {step === 0 && (
            <>
              <p className="text-sm text-slate-500">{t("wiz.h0")}</p>
              <div className="flex flex-wrap gap-2" data-testid="activities-group">
                {cat.activities.map((x) => (
                  <Chip key={x.id} on={a.activities.includes(x.id)} data-testid={`activity-${x.id}`} onClick={() => toggle("activities", x.id)}>
                    {x.label}
                  </Chip>
                ))}
              </div>
              {a.activities.includes("altro") && (
                <Input placeholder={t("wiz.other")} value={a.other_text} onChange={(e) => setA({ ...a, other_text: e.target.value })} data-testid="other-activity-input" />
              )}
            </>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="services-group">
              {cat.services.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-slate-200 p-4" data-testid={`service-${s.id}`}>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{s.label}</span>
                    <span className="block text-xs text-slate-500">{s.desc}</span>
                  </span>
                  <Switch checked={a.services.includes(s.id)} onCheckedChange={() => toggle("services", s.id)} data-testid={`service-switch-${s.id}`} />
                </label>
              ))}
            </div>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-slate-500">{t("wiz.h2")}</p>
              <div className="flex flex-wrap gap-2" data-testid="departments-group">
                {cat.departments.map((d) => (
                  <Chip key={d} on={a.departments.includes(d)} data-testid={`dept-${d}`} onClick={() => toggle("departments", d)}>
                    {d}
                  </Chip>
                ))}
              </div>
              <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-4">
                <span>
                  <span className="block text-sm font-semibold">{t("wiz.menu.t")}</span>
                  <span className="block text-xs text-slate-500">{t("wiz.menu.d")}</span>
                </span>
                <Switch checked={a.preload_menu} onCheckedChange={(v) => setA({ ...a, preload_menu: v })} data-testid="preload-menu-switch" />
              </label>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm text-slate-500">{t("wiz.h3")}</p>
              <div className="flex flex-wrap gap-2" data-testid="zones-group">
                {cat.zone_presets.map((z) => (
                  <Chip key={z} on={a.zones.includes(z)} data-testid={`zone-${z}`} onClick={() => toggle("zones", z)}>
                    {z}
                  </Chip>
                ))}
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="flex flex-wrap gap-2" data-testid="team-group">
                {cat.team_sizes.map((x) => (
                  <Chip key={x} on={a.team_size === x} data-testid={`team-${x}`} onClick={() => setA({ ...a, team_size: x })}>
                    {x === "1" ? t("wiz.team.solo") : x === ">10" ? t("wiz.team.more") : t("wiz.team.people", { n: x })}
                  </Chip>
                ))}
              </div>
              <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-4">
                <span>
                  <span className="block text-sm font-semibold">{t("wiz.float.t")}</span>
                  <span className="block text-xs text-slate-500">{t("wiz.float.d")}</span>
                </span>
                <Switch checked={a.floats_per_person} data-testid="floats-switch"
                        onCheckedChange={(v) => setA({ ...a, floats_per_person: v })} />
              </label>
              {a.floats_per_person && (
                <div className="flex flex-wrap items-end gap-3 rounded-lg bg-slate-50 p-4">
                  <div>
                    <Label className="text-xs">{t("wiz.float.amount")}</Label>
                    <Input className="mt-1 h-11 w-28" type="number" inputMode="numeric" min="0" step="10"
                           value={a.float_amount === 0 ? "" : a.float_amount} placeholder="50"
                           data-testid="float-amount-input"
                           onChange={(e) => {
                             const v = e.target.value.replace(/[^\d]/g, "");
                             setA({ ...a, float_amount: v === "" ? 0 : Number(v) });
                           }} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("wiz.float.outdoor")}</Label>
                    <Input className="mt-1 h-11 w-24" type="number" inputMode="numeric" min="0"
                           value={a.outdoor_people === 0 ? "" : a.outdoor_people} placeholder="0"
                           data-testid="outdoor-people-input"
                           onChange={(e) => {
                             const v = e.target.value.replace(/[^\d]/g, "");
                             setA({ ...a, outdoor_people: v === "" ? 0 : Number(v) });
                           }} />
                  </div>
                  <p className="max-w-xs text-xs text-slate-500">{t("wiz.float.note")}</p>
                </div>
              )}
            </>
          )}

          {step === 5 && (
            <>
              <p className="text-sm text-slate-500">{t("wiz.h5")}</p>
              <div className="space-y-3" data-testid="extras-group">
                {["menu_fisso", "carta_qr", "cassa_fiscale"].map((id) => (
                  <label
                    key={id}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-slate-200 p-4"
                    data-testid={`extra-${id}`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{t(`wiz.extra.${id}.t`)}</span>
                      <span className="block text-xs text-slate-500">{t(`wiz.extra.${id}.d`)}</span>
                    </span>
                    <Switch
                      checked={a.extras.includes(id)}
                      onCheckedChange={() => toggle("extras", id)}
                      data-testid={`extra-switch-${id}`}
                    />
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500">{t("wiz.extra.note")}</p>

              <div className="space-y-3 border-t pt-4" data-testid="banco-group">
                <p className="text-sm font-semibold">{t("wiz.banco.q")}</p>
                {["tavola-calda", "tavola-fredda"].map((id) => (
                  <label
                    key={id}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-slate-200 p-4"
                    data-testid={`banco-${id}`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{t(`wiz.banco.${id}.t`)}</span>
                      <span className="block text-xs text-slate-500">{t(`wiz.banco.${id}.d`)}</span>
                    </span>
                    <Switch
                      checked={a.banco.includes(id)}
                      onCheckedChange={() => toggle("banco", id)}
                      data-testid={`banco-switch-${id}`}
                    />
                  </label>
                ))}
              </div>
            </>
          )}

          {step === 6 && preview && (
            <div className="space-y-4" data-testid="setup-summary">
              <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">{t("wiz.plan")}</p>
                <p className="mt-1 text-xl font-bold" data-testid="suggested-plan">{preview.suggested_plan_name}</p>
                <ul className="mt-2 space-y-0.5">
                  {preview.plan_reason.map((r) => (
                    <li key={r} className="flex items-center gap-1.5 text-xs text-amber-900">
                      <Check className="h-3 w-3" /> {r}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  [t("wiz.sum.act"), preview.activity_labels.join(", ") || "—"],
                  [t("wiz.sum.serv"), preview.services.join(", ") || "—"],
                  [t("wiz.sum.dept"), preview.departments.join(", ")],
                  [t("wiz.sum.zones"), preview.zones.join(", ")],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-slate-200 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
                    <p className="mt-1 text-sm font-medium">{v}</p>
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-xs">{t("wiz.sum.mods")}</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="summary-modules">
                  {preview.modules.map((m) => (
                    <span key={m} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                      {cat.modules.find((x) => x.id === m)?.label || m}
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">{t("wiz.sum.note")}</p>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                <Label className="text-xs">{t("wiz.motto.t")}</Label>
                <Input className="mt-1.5 h-11 bg-white" maxLength={120} placeholder={t("wiz.motto.ph")}
                       value={a.motto} data-testid="motto-input"
                       onChange={(e) => setA({ ...a, motto: e.target.value })} />
                <p className="mt-1.5 text-[11px] text-slate-500">{t("wiz.motto.d")}</p>
              </div>

              {publicMode && (
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <Input className="h-11 bg-white" placeholder={t("wiz.name")} value={bizName} onChange={(e) => setBizName(e.target.value)} data-testid="trial-name-input" />
                  <Input className="h-11 bg-white" placeholder={t("wiz.vat")} value={vat} onChange={(e) => setVat(e.target.value)} data-testid="trial-vat-input" />
                  <p className="rounded-md border border-slate-200 bg-white p-2.5 text-[11px] leading-relaxed text-slate-600" data-testid="trial-vat-note">
                    {t("wiz.vat.note")}
                  </p>
                  <Input className="h-11 bg-white" type="email" placeholder={t("wiz.email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="trial-email-input" />
                  <p className="text-[11px] text-slate-500">{t("wiz.trial.note")}</p>
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-slate-200 bg-white p-3">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-slate-900"
                      checked={accept}
                      onChange={(e) => setAccept(e.target.checked)}
                      data-testid="accept-terms-check"
                    />
                    <span className="text-[11px] leading-relaxed text-slate-600">
                      {t("wiz.accept.pre")}{" "}
                      <a href="/termini" target="_blank" rel="noreferrer" className="font-semibold text-slate-900 underline" data-testid="accept-terms-link">
                        {t("wiz.accept.terms")}
                      </a>{" "}
                      {t("wiz.accept.and")}{" "}
                      <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-slate-900 underline" data-testid="accept-privacy-link">
                        {t("wiz.accept.privacy")}
                      </a>
                      . {t("wiz.accept.beta")}
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="setup-back-btn">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("wiz.back")}
            </Button>
            {step < STEPS - 1 ? (
              <Button className="h-11" disabled={busy} onClick={next} data-testid="setup-next-btn">
                {t("wiz.next")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            ) : publicMode ? (
              <Button className="h-11" disabled={busy} onClick={startTrial} data-testid="start-trial-btn">
                <Sparkles className="mr-1.5 h-4 w-4" /> {t("wiz.trial")}
              </Button>
            ) : (
              <Button className="h-11" disabled={busy} onClick={apply} data-testid="setup-apply-btn">
                <Sparkles className="mr-1.5 h-4 w-4" /> {t("wiz.apply")}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default function Setup({ onDone }) {
  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10" data-testid="setup-page">
      <SetupWizard onDone={onDone} />
    </div>
  );
}
